import type { PropsWithChildren } from 'react'
import { Toaster } from '@ds/components'
import { AppLayout } from '@ds/layouts'
import { SiteHeaderContainer } from '@features/site-header'
import { AsyncSiteFooterContainer } from '@features/site-footer'
import { CookieConsentContainer } from '@features/cookie-consent'
import { AsyncAudioPlayerContainer } from '@features/audio-player'
import { UploadFlowProvider } from '@features/audio-upload'
import { AsyncGoogleOneTapContainer, useUnauthorizedLogout } from '@features/auth'
import { HqStoragePaywallFlowRunner } from '@features/paywall'
import { RouteProgressContainer } from '@features/route-progress'
import { useCurrentUser } from '@features/session'
import { ScreenErrorBoundary } from '../screen/ScreenErrorBoundary'
import { AppAnalyticsContainer } from './AppAnalyticsContainer'
import { AuthIntentRunner } from './AuthIntentRunner'
import { CheckoutRedirectRunner } from './CheckoutRedirectRunner'
import { ModalHost } from './ModalHost'
import { PurchaseSuccessRunner } from './PurchaseSuccessRunner'
import { QueuedGiftRunner } from './QueuedGiftRunner'
import { RegPaywallRunner } from './RegPaywallRunner'
import { SessionDialogEvaluator } from './SessionDialogEvaluator'
import { useIsChromeless } from './useIsChromeless'

export const AppLayoutContainer = ({ children }: PropsWithChildren) => {
    useUnauthorizedLogout()

    const isChromeless = useIsChromeless()
    const { isAuthenticated } = useCurrentUser()
    const header = isChromeless ? null : <SiteHeaderContainer />
    const footer = isChromeless ? null : <AsyncSiteFooterContainer />

    return (
        // The upload flow wraps the layout, not sits inside it: the header's Upload button reads it
        // through context, and the picked files must outlive both the drawer and a soft navigation.
        <UploadFlowProvider>
            <AppLayout
                header={header}
                footer={footer}
                overlays={
                    <>
                        <RouteProgressContainer />
                        <AsyncAudioPlayerContainer />
                        <Toaster theme="system" />
                    </>
                }
            >
                <ScreenErrorBoundary>{children}</ScreenErrorBoundary>
                <AppAnalyticsContainer />
                <CookieConsentContainer />
                <RegPaywallRunner />
                <HqStoragePaywallFlowRunner />
                <AuthIntentRunner />
                <CheckoutRedirectRunner />
                <PurchaseSuccessRunner />
                <SessionDialogEvaluator />
                <QueuedGiftRunner />
                {isChromeless || isAuthenticated ? null : <AsyncGoogleOneTapContainer />}
            </AppLayout>
            <ModalHost />
        </UploadFlowProvider>
    )
}
